// Orientation 2026-2027 — King's Academy (React)
// Self-contained. Default export, no required props.
// Landing: pick school, then pick who you are. Schedule is one dataset, filtered by audience.

import { useState, useEffect } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";

const DAY_START = 480;   // 8:00 am
const DAY_END = 1380;    // 11:00 pm
const PPM = 1.8;         // pixels per minute
const TOP_PAD = 18;
const MOBILE_BP = 768;

const HT = (s, e) => (e - s) * PPM;
const overlaps = (a, b) => a.s < b.e && b.s < a.e;

const LANES = {
  boarding: { l: 0, w: 33 },
  day: { l: 34, w: 26 },
  shared: { l: 0, w: 60 },
  parents: { l: 62, w: 18 },
  uco: { l: 62, w: 18 },
};
const MS_X = { boarding: 0, parents: 62, bus: 74 };

const COLORS = {
  navy: "#002D62", red: "#EE3124", gold: "#A39161", beige: "#E8E6DF",
  navyTint: "#EAEFF6", redTint: "#FBE9E8", redInk: "#9b1a16",
  sharedFill: "#E8E6DF", parentsFill: "#E9DDBC", parentsEdge: "#7e6f44", parentsInk: "#5f5230",
  grid: "#e8e3d8",
};

const LANE_STYLE = {
  boarding: { bg: COLORS.navyTint, edge: COLORS.navy, ink: COLORS.navy, tag: "Boarding" },
  day: { bg: COLORS.redTint, edge: COLORS.red, ink: COLORS.redInk, tag: "Day" },
  shared: { bg: COLORS.sharedFill, edge: COLORS.gold, ink: COLORS.navy, tag: "All students" },
  parents: { bg: COLORS.parentsFill, edge: COLORS.parentsEdge, ink: COLORS.parentsInk, tag: "Parents" },
  uco: { bg: COLORS.sharedFill, edge: COLORS.gold, ink: COLORS.navy, tag: "Grade 12" },
};

// Staff-only blocks get a dark slate treatment so they read as clearly not-for-students.
const FACULTY_STYLE = { bg: "#333B49", edge: "#A39161", ink: "#FFFFFF", tag: "Faculty" };

const FONT_BODY = '"Helvetica Neue", Helvetica, Arial, "Segoe UI", system-ui, sans-serif';
const FONT_DISPLAY = '"Clarice", Georgia, "Times New Roman", serif';

// ---- audiences ----
const NB = "newBoarding", ND = "newDay", RB = "returningBoarding", RD = "returningDay";
const ALL4 = [NB, ND, RB, RD];
const NEW2 = [NB, ND];
const BOARDERS = [NB, RB];
const DAYERS = [ND, RD];

const AUDIENCES = [
  { key: NB, label: "New Boarding Student", note: "First year at King's, living on campus" },
  { key: ND, label: "New Day Student", note: "First year at King's, commuting" },
  { key: RB, label: "Returning Boarding Student", note: "Returning, living on campus" },
  { key: RD, label: "Returning Day Student", note: "Returning, commuting" },
  { key: "all", label: "Full Master Schedule", note: "The full master schedule" },
];
const audLabel = (k) => (AUDIENCES.find((a) => a.key === k) || {}).label || "Everyone";

const DEFAULT_HEADCOLS = [
  { at: 0, to: 33, label: "Boarding", bg: COLORS.navy },
  { at: 33, to: 60, label: "Day", bg: COLORS.red },
  { at: 60, to: 74, label: "Parents", bg: COLORS.parentsEdge },
  { at: 74, to: 100, label: "Buses", bg: COLORS.gold },
];

function hour12(m) {
  const h = Math.floor(m / 60);
  const ap = (h % 24) < 12 ? "AM" : "PM";
  let hh = h % 12;
  if (hh === 0) hh = 12;
  return hh + " " + ap;
}

function packLane(events) {
  const ev = events.map((e, i) => ({ ...e, _i: i })).sort((a, b) => a.s - b.s || a.e - b.e);
  const colEnd = [];
  ev.forEach((x) => {
    let placed = false;
    for (let i = 0; i < colEnd.length; i++) {
      if (colEnd[i] <= x.s) { x.col = i; colEnd[i] = x.e; placed = true; break; }
    }
    if (!placed) { x.col = colEnd.length; colEnd.push(x.e); }
  });
  ev.forEach((x) => (x.cl = -1));
  let c = 0;
  for (const a of ev) {
    if (a.cl < 0) {
      a.cl = c;
      let changed = true;
      while (changed) {
        changed = false;
        for (const b of ev) if (b.cl >= 0)
          for (const d of ev) if (d.cl < 0 && overlaps(b, d)) { d.cl = b.cl; changed = true; }
      }
      c++;
    }
  }
  const counts = {};
  ev.forEach((x) => (counts[x.cl] = Math.max(counts[x.cl] || 0, x.col + 1)));
  ev.forEach((x) => (x.of = counts[x.cl]));
  return ev;
}

// ---- data: one source of truth; `for` lists which audiences see each item ----
const DAYS = [
  {
    meta: "Friday · August 14, 2026",
    title: "New International Student Houses Open and International Family Reception",
    pop: "",
    intl: true,
    axisStart: 720,
    axisEnd: 1140,
    wide: true,
    dress: "Casual",
    headCols: [{ at: 0, to: 100, label: "International Families and Students", bg: COLORS.navy }],
    events: [
      { s: 780, e: 960, lane: "shared", t: "1:00 – 4:00 pm", l: "Houses Open", w: "New boarding international students", for: [NB], tag: "New boarding international" },
      { s: 1020, e: 1140, lane: "shared", t: "5:00 – 7:00 pm", l: "Reception", w: "New and returning international families", n: "Safwan M. Masri Courtyard · casual dress", for: [NB, RB], tag: "International families" },
    ],
    milestones: [],
  },
  {
    meta: "Saturday · August 15, 2026",
    title: "Day 1 — New Students",
    pop: "New boarding and new day students",
    dress: "Casual",
    events: [
      { s: 510, e: 720, lane: "boarding", t: "8:30 am – 12:00 pm", l: "New Boarding Students Registration", n: "First report to the New Dining Hall", for: [NB], withParents: true },
      { s: 630, e: 720, lane: "day", t: "10:30 am – 12:00 pm", l: "New Day Students Registration", n: "First report to the New Dining Hall", for: [ND], withParents: true },
      { s: 720, e: 765, lane: "shared", t: "12:00 – 12:45 pm", l: "Walk-through Lunch", n: "Hess Family Dining Hall", for: NEW2, withParents: true },
      { s: 780, e: 855, lane: "shared", t: "1:00 – 2:15 pm", l: "Head of School Welcome", w: "Mrs. Penny Townsend · Includes IT session", n: "Abdul Majeed Shoman Auditorium", for: NEW2, withParents: true },
      { s: 855, e: 900, lane: "shared", t: "2:15 – 3:00 pm", l: "Meet Your Student's Advisor", w: "Families gather by grade, then are led to their student's advisor", n: "Grade 9: Abdul Majeed Shoman Auditorium · Grade 10: Gallery · Grades 11 and 12: Library", for: NEW2, withParents: true, parentNote: "University Counseling is also in the Hess Family Dining Hall for questions." },
      { s: 900, e: 960, lane: "shared", t: "3:00 – 4:00 pm", l: "Ice Cream Social and Introductions", w: "Head to your Houses", for: NEW2 },
      { s: 960, e: 1080, lane: "shared", t: "4:00 – 6:00 pm", l: "Grade Level Skits", n: "Abdul Majeed Shoman Auditorium", for: NEW2 },
      { s: 1080, e: 1140, lane: "shared", t: "6:00 – 7:00 pm", l: "BBQ Dinner", n: "The Weinberg Family Courtyard, Behind the Student Union", for: NEW2 },
      { s: 1140, e: 1260, lane: "boarding", t: "7:00 – 9:00 pm", l: "Games and Activities", for: [NB] },
      { s: 1290, e: 1350, lane: "boarding", t: "9:30 – 10:30 pm", l: "Return to Houses for Meetings", for: [NB] },
    ],
    milestones: [
      { at: 900, kind: "parents", time: "3:00 pm", label: "Parents Depart", for: NEW2 },
      { at: 1155, kind: "bus", time: "7:15 pm", label: "Early bus departs", for: [ND] },
      { at: 1260, kind: "bus", time: "9:00 pm", label: "Late bus departs", for: [ND] },
      { at: 1350, kind: "boarding", time: "10:30 pm", label: "Lights Out", for: [NB] },
    ],
  },
  {
    meta: "Sunday · August 16, 2026",
    title: "Day 2 — All Students",
    pop: "Returning students arrive · new students continue orientation",
    dress: "Casual",
    subnote: "New boarding and new day students checked in on Saturday. There is no check-in for you today.",
    events: [
      { s: 510, e: 680, lane: "boarding", t: "8:30 – 11:20 am", l: "Returning Boarding Students Registration", n: "First report to the New Dining Hall", for: [RB], tag: "Returning", withParents: true },
      { s: 480, e: 600, lane: "shared", t: "8:00 – 10:00 am", l: "Breakfast", for: NEW2, tag: "All new students" },
      { s: 540, e: 680, lane: "day", t: "9:00 – 11:20 am", l: "Returning Day Students Registration", w: "Parent required", n: "First report to the New Dining Hall", for: [RD], tag: "Returning", withParents: true },
      { s: 600, e: 660, lane: "shared", t: "10:00 – 11:00 am", l: "New Student Programming", for: NEW2, tag: "All new students" },
      { s: 690, e: 720, lane: "shared", t: "11:30 am – 12:00 pm", l: "Head of School Welcome", w: "Mrs. Penny Townsend", n: "Abdul Majeed Shoman Auditorium", for: ALL4, withParents: true },
      { s: 540, e: 600, lane: "uco", t: "9:00 – 10:00 am", l: "UCO Meeting — Grade 12", n: "Required for parents and students · Abdul Majeed Shoman Auditorium", for: ALL4 },
      { s: 740, e: 780, lane: "shared", t: "12:20 – 1:00 pm", l: "Walk-through Lunch", for: ALL4 },
      { s: 780, e: 870, lane: "shared", t: "1:00 – 2:30 pm", l: "Grade Level Meetings — Grades 9–11", for: ALL4 },
      { s: 870, e: 930, lane: "shared", t: "2:30 – 3:30 pm", l: "Advisory", for: ALL4 },
      { s: 930, e: 960, lane: "shared", t: "3:30 – 4:00 pm", l: "Refreshments", n: "Refectory Square", for: ALL4 },
      { s: 960, e: 1080, lane: "shared", t: "4:00 – 6:00 pm", l: "Games and Activities", n: "Athletic Facilities", for: ALL4 },
      { s: 1080, e: 1170, lane: "shared", t: "6:00 – 7:30 pm", l: "International Food Fest", for: ALL4 },
      { s: 1200, e: 1260, lane: "boarding", t: "8:00 – 9:00 pm", l: "Residential Life Meeting", n: "Abdul Majeed Shoman Auditorium", for: BOARDERS },
      { s: 1260, e: 1320, lane: "boarding", t: "9:00 – 10:00 pm", l: "Refectory Square", n: "Kerak tea and biscuits · cards, games, cornhole, etc.", for: BOARDERS },
    ],
    milestones: [
      { at: 510, kind: "bus", time: "8:30 am", label: "Buses Depart from Amman", w: "New day students", for: [ND] },
      { at: 720, kind: "parents", time: "12:00 pm", label: "Parents Depart", for: [RB, RD] },
      { at: 1170, kind: "bus", time: "7:30 pm", label: "Day Bus Returns to Amman", for: DAYERS },
      { at: 1320, kind: "boarding", time: "10:00 pm", label: "Return to Houses", for: BOARDERS },
      { at: 1350, kind: "boarding", time: "10:30 pm", label: "Lights Out", for: BOARDERS },
    ],
  },
  {
    meta: "Monday · August 17, 2026",
    title: "Day 3 — All Students",
    pop: "Block schedule and senior celebration",
    headCols: [{ at: 0, to: 100, label: "All Students", bg: COLORS.navy }],
    wide: true,
    axisEnd: 1185,
    dress: "All students must be in uniform",
    events: [
      { s: 480, e: 540, lane: "shared", t: "8:00 – 9:00 am", l: "Faculty Meeting", tag: "Faculty", for: ALL4 },
      { s: 480, e: 540, lane: "shared", t: "8:00 – 9:00 am", l: "Breakfast", for: BOARDERS },
      { s: 540, e: 600, lane: "shared", t: "9:00 – 10:00 am", l: "Grade 9–11 Class Meetings", tag: "Grades 9–11", for: ALL4 },
      { s: 615, e: 645, lane: "shared", t: "10:15 – 10:45 am", l: "Seniors Arrive to Campus", tag: "Grade 12", for: ALL4 },
      { s: 660, e: 680, lane: "shared", t: "11:00 – 11:20 am", l: "Block A", for: ALL4 },
      { s: 685, e: 705, lane: "shared", t: "11:25 – 11:45 am", l: "Block B", for: ALL4 },
      { s: 710, e: 730, lane: "shared", t: "11:50 am – 12:10 pm", l: "Block C", for: ALL4 },
      { s: 735, e: 755, lane: "shared", t: "12:15 – 12:35 pm", l: "Block D", for: ALL4 },
      { s: 760, e: 805, lane: "shared", t: "12:40 – 1:25 pm", l: "Lunch", for: ALL4 },
      { s: 810, e: 830, lane: "shared", t: "1:30 – 1:50 pm", l: "Block E", for: ALL4 },
      { s: 835, e: 855, lane: "shared", t: "1:55 – 2:15 pm", l: "Block F", for: ALL4 },
      { s: 860, e: 880, lane: "shared", t: "2:20 – 2:40 pm", l: "Block G", for: ALL4 },
      { s: 885, e: 915, lane: "shared", t: "2:45 – 3:15 pm", l: "H/I block", for: ALL4 },
      { s: 930, e: 1005, lane: "shared", t: "3:30 – 4:45 pm", l: "Games and Activities", for: ALL4 },
    ],
    milestones: [
      { at: 540, kind: "bus", time: "9:00 am", label: "Day Students Arrive", for: DAYERS },
      { at: 1035, kind: "bus", time: "5:15 pm", label: "Bus departs", for: DAYERS },
      { at: 1170, kind: "bus", time: "7:30 pm", label: "Late bus departs", for: DAYERS },
    ],
  },
];

// Parent items carry their own `for` audiences and filter like everything else.
const isParentItem = (x) => x.lane === "parents" || x.kind === "parents";
function visibleFor(x, aud) {
  if (aud === "all") return true;
  return Array.isArray(x.for) && x.for.includes(aud);
}
function filterDay(day, aud) {
  return {
    ...day,
    events: (day.events || []).filter((e) => visibleFor(e, aud)),
    milestones: (day.milestones || []).filter((m) => visibleFor(m, aud)),
  };
}
const dayIsEmpty = (d) => (d.events || []).length === 0 && (d.milestones || []).length === 0;

function Logo({ height = 46 }) {
  return <img src="/ka-logo.png" alt="King's Academy" style={{ height, width: "auto", display: "block", alignSelf: "flex-start", flex: "none" }} />;
}

function useIsMobile(bp = MOBILE_BP) {
  const [m, setM] = useState(typeof window !== "undefined" ? window.innerWidth < bp : false);
  useEffect(() => {
    const onResize = () => setM(window.innerWidth < bp);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [bp]);
  return m;
}

function LegendItem({ swatch, children }) {
  return <span style={{ display: "flex", alignItems: "center", gap: 8 }}>{swatch}{children}</span>;
}

function Swatch({ solid, dashed, dot }) {
  if (dot) return <span style={{ width: 14, height: 14, borderRadius: "50%", background: "#fff", border: `2px solid ${COLORS.navy}` }} />;
  if (dashed) return <span style={{ width: 26, height: 14, borderRadius: 3, background: "#fff", border: `1px dashed ${COLORS.gold}` }} />;
  return <span style={{ width: 26, height: 14, borderRadius: 3, background: solid }} />;
}

/* ---------------- Desktop timeline ---------------- */

function EventBlock({ ev, lane, left, width, delay, y, ht }) {
  const tight = width < 24;
  const s = ev.tag === "Faculty" ? FACULTY_STYLE : LANE_STYLE[lane];
  return (
    <div className="evt" style={{
      position: "absolute", top: y(ev.s), minHeight: ht(ev.s, ev.e) - 4,
      left: `${left}%`, width: `${width}%`,
      borderRadius: 5, padding: "5px 9px", overflow: "visible", overflowWrap: "anywhere",
      background: s.bg, border: "1px solid rgba(35,60,95,.10)", borderLeft: `4px solid ${s.edge}`, color: s.ink,
      boxShadow: ev.tag ? "0 1px 3px rgba(35,60,95,.14)" : "0 1px 2px rgba(35,60,95,.09)",
      outline: ev.tag ? `2px solid ${s.edge}` : "none", outlineOffset: -1,
      animationDelay: `${delay}ms`,
    }}>
      <div style={{ fontSize: tight ? 9.5 : 10, fontWeight: 700, letterSpacing: ".02em", opacity: 0.78, lineHeight: 1.05 }}>
        {ev.t}
        {ev.withParents && <span style={{ display: "inline-block", marginLeft: 6, background: COLORS.parentsEdge, color: "#fff", fontSize: 9, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase", padding: "1px 5px", borderRadius: 3, verticalAlign: "middle" }}>+ Parents</span>}
      </div>
      {ev.tag && (
        <div style={{
          display: "inline-block", margin: "2px 0 3px",
          background: s.edge, color: "#fff",
          fontSize: tight ? 9.5 : 11, fontWeight: 700,
          letterSpacing: ".1em", textTransform: "uppercase",
          padding: "2px 8px", borderRadius: 3, lineHeight: 1.3,
        }}>{ev.tag}</div>
      )}
      <div style={{ fontSize: tight ? 11.5 : 12.5, fontWeight: 700, lineHeight: 1.12 }}>{ev.l}</div>
      {ev.w && <div style={{ fontSize: tight ? 10 : 10.5, fontStyle: "italic", opacity: 0.82, marginTop: 1, lineHeight: 1.1 }}>{ev.w}</div>}
      {ev.n && <div style={{ fontSize: 10.5, opacity: 0.82, marginTop: 1, lineHeight: 1.15 }}>{ev.n}</div>}
    </div>
  );
}

function Milestone({ ms, delay, y }) {
  const x = MS_X[ms.kind];
  const ink = ms.kind === "bus" ? "#8a7c52" : ms.kind === "parents" ? COLORS.parentsInk : COLORS.navy;
  const ring = ms.kind === "boarding" ? COLORS.navy : COLORS.gold;
  return (
    <div className="ms" style={{ position: "absolute", top: y(ms.at) + 3, left: `${x}%`, display: "flex", alignItems: "center", gap: 6, zIndex: 5, animationDelay: `${delay}ms` }}>
      <span style={{ width: 12, height: 12, borderRadius: "50%", background: "#fff", border: `2.5px solid ${ring}`, flex: "none" }} />
      <span style={{ fontSize: 11, fontWeight: 700, color: ink, whiteSpace: "nowrap", background: "rgba(255,255,255,.92)", border: ms.kind === "bus" ? `1px dashed ${COLORS.gold}` : "1px solid #ece6d8", borderRadius: 4, padding: "1px 7px" }}>
        {ms.label}
        <span style={{ color: "#9b9384", fontWeight: 700, marginLeft: 6, fontSize: 10.5 }}>{ms.time}</span>
        {ms.w && <span style={{ color: "#9b9384", fontWeight: 700, marginLeft: 6, fontSize: 10.5 }}>· {ms.w}</span>}
      </span>
    </div>
  );
}

function TimelineDay({ day, aud }) {
  let delay = 0;
  const aStart = day.axisStart ?? DAY_START;
  const aEnd = day.axisEnd ?? DAY_END;
  const ppm = day.ppm || PPM;
  const y = (m) => (m - aStart) * ppm + TOP_PAD;
  const ht = (s, e) => (e - s) * ppm;
  const totalH = (aEnd - aStart) * ppm + TOP_PAD + 30;
  let headCols = day.headCols || DEFAULT_HEADCOLS;
  if (!day.wide && !day.headCols) {
    if (aud === NB || aud === RB) {
      headCols = [
        { at: 0, to: 60, label: "Boarding", bg: COLORS.navy },
        { at: 60, to: 74, label: "Parents", bg: COLORS.parentsEdge },
        { at: 74, to: 100, label: "Buses", bg: COLORS.gold },
      ];
    } else if (aud === ND || aud === RD) {
      headCols = [
        { at: 0, to: 60, label: "Day", bg: COLORS.red },
        { at: 60, to: 74, label: "Parents", bg: COLORS.parentsEdge },
        { at: 74, to: 100, label: "Buses", bg: COLORS.gold },
      ];
    }
  }
  const lanes = day.wide ? { ...LANES, shared: { l: 0, w: 100 } } : LANES;

  const grid = [];
  for (let m = aStart; m <= aEnd; m += 60) {
    grid.push(<div key={"h" + m} style={{ position: "absolute", left: "var(--gutter)", right: 0, top: y(m), borderTop: `1px solid ${COLORS.grid}` }} />);
    grid.push(<div key={"hl" + m} style={{ position: "absolute", left: 0, width: "var(--gutter)", paddingRight: 11, textAlign: "right", top: y(m), transform: "translateY(-7px)", fontSize: 11, color: "#9b9384", fontWeight: 700 }}>{hour12(m)}</div>);
  }
  for (let m = aStart + 30; m < aEnd; m += 60) {
    grid.push(<div key={"hf" + m} style={{ position: "absolute", left: "var(--gutter)", right: 0, top: y(m), borderTop: "1px solid #f1ecdf" }} />);
  }
  headCols.forEach((c) => {
    if (c.at > 0) grid.push(<div key={"v" + c.at} style={{ position: "absolute", top: 0, bottom: 0, left: `calc(var(--gutter) + (100% - var(--gutter) - 8px) * ${c.at / 100})`, borderLeft: "1px dashed #efeadf" }} />);
  });
  day.milestones.forEach((ms, i) => {
    grid.push(<div key={"r" + i} style={{ position: "absolute", left: "var(--gutter)", right: 0, top: y(ms.at), borderTop: "1px dotted #d6cfbf" }} />);
  });

  const GAP = 0.6;
  const nonSide = day.events.filter((e) => e.lane !== "parents" && e.lane !== "uco").map((e, i) => ({ ...e, __id: i }));
  const sideEvents = day.events.filter((e) => e.lane === "parents" || e.lane === "uco");
  const blocks = [];

  if (day.wide) {
    packLane(nonSide).forEach((ev, i) => {
      const w = 100 / ev.of;
      blocks.push(<EventBlock key={"m" + i} ev={ev} lane={ev.lane} left={ev.col * w} width={w - (ev.of > 1 ? GAP : 0)} delay={delay} y={y} ht={ht} />);
      delay += 30;
    });
  } else {
    const bPack = {}; packLane(nonSide.filter((e) => e.lane === "boarding" || e.lane === "shared")).forEach((e) => { bPack[e.__id] = { col: e.col, of: e.of }; });
    const dPack = {}; packLane(nonSide.filter((e) => e.lane === "day" || e.lane === "shared")).forEach((e) => { dPack[e.__id] = { col: e.col, of: e.of }; });
    nonSide.forEach((e, i) => {
      if (e.lane === "boarding") {
        const { col, of } = bPack[e.__id]; const w = 33 / of;
        blocks.push(<EventBlock key={"blk" + i} ev={e} lane="boarding" left={col * w} width={w - (of > 1 ? GAP : 0)} delay={delay} y={y} ht={ht} />);
      } else if (e.lane === "day") {
        const { col, of } = dPack[e.__id]; const w = 26 / of;
        blocks.push(<EventBlock key={"blk" + i} ev={e} lane="day" left={34 + col * w} width={w - (of > 1 ? GAP : 0)} delay={delay} y={y} ht={ht} />);
      } else {
        const b = bPack[e.__id], d = dPack[e.__id];
        if (b.of === 1 && d.of === 1) {
          blocks.push(<EventBlock key={"blk" + i} ev={e} lane="shared" left={0} width={60} delay={delay} y={y} ht={ht} />);
        } else {
          const wb = 33 / b.of;
          blocks.push(<EventBlock key={"blkb" + i} ev={e} lane="shared" left={b.col * wb} width={wb - (b.of > 1 ? GAP : 0)} delay={delay} y={y} ht={ht} />);
          const wd = 26 / d.of;
          blocks.push(<EventBlock key={"blkd" + i} ev={e} lane="shared" left={34 + d.col * wd} width={wd - (d.of > 1 ? GAP : 0)} delay={delay} y={y} ht={ht} />);
        }
      }
      delay += 30;
    });
  }
  const sideAll = sideEvents.map((e) => ({ ...e }));
  const wp = day.events.filter((e) => e.withParents);
  const regs = wp.filter((e) => /Registration/.test(e.l));
  if (regs.length) {
    sideAll.push({ s: Math.min(...regs.map((e) => e.s)), e: Math.max(...regs.map((e) => e.e)), lane: "parents", l: "Registration", n: "Parents attend with their student" });
  }
  const parentsLane = sideEvents.filter((e) => e.lane === "parents");
  wp.filter((e) => !/Registration/.test(e.l)).forEach((e) => {
    if (parentsLane.some((pe) => pe.s < e.e && e.s < pe.e)) return;
    sideAll.push({ s: e.s, e: e.e, lane: "parents", l: e.l, n: e.parentNote !== undefined ? e.parentNote : e.n });
  });
  if (sideAll.length) {
    const region = lanes.parents;
    packLane(sideAll).forEach((ev, i) => {
      const w = region.w / ev.of;
      blocks.push(<EventBlock key={"side" + i} ev={ev} lane={ev.lane} left={region.l + ev.col * w} width={w - (ev.of > 1 ? GAP : 0)} delay={delay} y={y} ht={ht} />);
      delay += 30;
    });
  }
  const milestoneEls = day.milestones.map((ms, i) => {
    const el = <Milestone key={"m" + i} ms={ms} delay={delay} y={y} />;
    delay += 30;
    return el;
  });

  return (
    <div style={{ overflowX: "auto" }}>
      <div style={{ minWidth: 820 }}>
        <div style={{ position: "relative", height: 34, background: "#fbf8f1", border: `1px solid ${COLORS.grid}`, borderTop: "none", fontSize: 11, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase" }}>
          <div style={{ position: "absolute", left: "var(--gutter)", right: 8, top: 0, bottom: 0 }}>
            {headCols.map((c) => (
              <div key={c.label} style={{
                position: "absolute", top: 4, bottom: 4, left: `${c.at}%`,
                width: `calc(${(c.to ?? 100) - c.at}% - 3px)`, background: c.bg || COLORS.navy,
                color: "#fff", borderRadius: 3, display: "flex", alignItems: "center",
                paddingLeft: 9, overflow: "hidden", whiteSpace: "nowrap",
              }}>{c.label}</div>
            ))}
          </div>
        </div>
        <div className="board" style={{ position: "relative", background: "#fff", border: `1px solid ${COLORS.grid}`, borderTop: "none", borderRadius: "0 0 6px 6px", overflow: "hidden", height: totalH }}>
          {grid}
          <div style={{ position: "absolute", left: "var(--gutter)", right: 8, top: 0, height: totalH }}>
            {blocks}
            {milestoneEls}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Mobile agenda ---------------- */

function AgendaDay({ day }) {
  const items = [];
  day.events.forEach((e) => items.push({ kind: "event", at: e.s, data: e }));
  day.milestones.forEach((m) => items.push({ kind: "ms", at: m.at, data: m }));
  items.sort((a, b) => a.at - b.at || ((a.kind === "ms" ? 0 : 1) - (b.kind === "ms" ? 0 : 1)));

  return (
    <div style={{ background: "#fff", border: `1px solid ${COLORS.grid}`, borderTop: "none", borderRadius: "0 0 6px 6px", padding: "4px 14px 10px" }}>
      {items.map((it, i) => {
        if (it.kind === "ms") {
          const ms = it.data;
          const ink = ms.kind === "bus" ? "#8a7c52" : ms.kind === "parents" ? COLORS.parentsInk : COLORS.navy;
          const ring = ms.kind === "boarding" ? COLORS.navy : COLORS.gold;
          return (
            <div key={"m" + i} style={{ display: "flex", alignItems: "center", gap: 9, padding: "9px 0", borderTop: i ? "1px solid #f0ece2" : "none" }}>
              <span style={{ width: 12, height: 12, borderRadius: "50%", background: "#fff", border: `2.5px solid ${ring}`, flex: "none" }} />
              <div style={{ fontSize: 13.5, fontWeight: 700, color: ink, ...(ms.kind === "bus" ? { border: `1px dashed ${COLORS.gold}`, borderRadius: 4, padding: "2px 8px" } : {}) }}>
                {ms.label}
                <span style={{ color: "#9b9384", marginLeft: 7, fontSize: 12 }}>{ms.time}</span>
                {ms.w && <span style={{ color: "#9b9384", marginLeft: 7, fontSize: 12, fontWeight: 600 }}>· {ms.w}</span>}
              </div>
            </div>
          );
        }
        const ev = it.data;
        const isFaculty = ev.tag === "Faculty";
        const s = isFaculty ? FACULTY_STYLE : LANE_STYLE[ev.lane];
        const subColor = isFaculty ? "rgba(255,255,255,.82)" : "#6b6557";
        return (
          <div key={"e" + i} style={{ display: "flex", gap: 10, padding: isFaculty ? "10px 12px" : "9px 0", margin: isFaculty ? "6px 0" : 0, background: isFaculty ? s.bg : "transparent", borderRadius: isFaculty ? 6 : 0, borderTop: (i && !isFaculty) ? "1px solid #f0ece2" : "none" }}>
            <div style={{ width: 4, borderRadius: 2, background: s.edge, flex: "none" }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: isFaculty ? "rgba(255,255,255,.7)" : "#9b9384", letterSpacing: ".02em" }}>
                {ev.t}
                <span style={{ color: s.ink, marginLeft: 8, textTransform: "uppercase", letterSpacing: ".06em" }}>{ev.tag || s.tag}</span>
                {ev.withParents && <span style={{ color: COLORS.parentsEdge, marginLeft: 8, textTransform: "uppercase", letterSpacing: ".06em" }}>+ Parents</span>}
              </div>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: s.ink, lineHeight: 1.18 }}>{ev.l}</div>
              {ev.w && <div style={{ fontSize: 12.5, fontStyle: "italic", color: subColor, marginTop: 1 }}>{ev.w}</div>}
              {ev.n && <div style={{ fontSize: 12.5, color: subColor, marginTop: 1 }}>{ev.n}</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function NothingScheduled({ nextLine }) {
  return (
    <div style={{ background: "#fff", border: `1px solid ${COLORS.grid}`, borderTop: "none", borderRadius: "0 0 6px 6px", padding: 16 }}>
      <div style={{ border: `1px dashed ${COLORS.gold}`, borderRadius: 6, padding: "18px 20px", background: "repeating-linear-gradient(135deg,#fbf7ee,#fbf7ee 9px,#f6f0e2 9px,#f6f0e2 18px)", color: "#6b6557", fontSize: 13.5, lineHeight: 1.5 }}>
        <div style={{ fontWeight: 700, color: COLORS.navy, marginBottom: 3 }}>Nothing scheduled for you on this day.</div>
        {nextLine}
      </div>
    </div>
  );
}

function DayCard({ day, open, onToggle, isMobile, aud }) {
  const empty = dayIsEmpty(day);
  return (
    <section className="day" style={{ marginTop: 22 }}>
      <div
        className="ofs-day-head" role="button" tabIndex={0} aria-expanded={open}
        onClick={onToggle}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(); } }}
        style={{
          background: COLORS.navy, color: "#fff", borderRadius: open ? "6px 6px 0 0" : 6,
          padding: "13px 18px", display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 14, cursor: "pointer", userSelect: "none", opacity: empty ? 0.72 : 1,
        }}
      >
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 16, flexWrap: "wrap", flex: 1, minWidth: 0 }}>
          <h2 style={{ fontFamily: FONT_DISPLAY, margin: 0, fontSize: 20, fontWeight: 700 }}>{day.meta}</h2>
          <div style={{ color: "#cdd6e2", fontSize: 13, fontWeight: 600, letterSpacing: ".03em" }}>
            <span style={{ color: COLORS.gold, fontWeight: 700 }}>{empty ? "Nothing scheduled for you" : (day.intl ? day.title : day.pop)}</span>
          </div>
        </div>
        <span style={{ flex: "none", fontSize: 13, color: COLORS.gold, transform: open ? "rotate(90deg)" : "none", transition: "transform .2s ease", display: "inline-block" }}>▶</span>
      </div>

      {open && (empty
        ? <NothingScheduled nextLine={day.emptyNote || "Check the other days for your schedule."} />
        : (
          <>
            {(day.dress || day.subnote) && (
              <div style={{ background: "#fbf8f1", borderLeft: `1px solid ${COLORS.grid}`, borderRight: `1px solid ${COLORS.grid}`, padding: "8px 14px", display: "flex", flexWrap: "wrap", alignItems: "center", gap: "4px 10px", fontSize: 12.5 }}>
                {day.dress && (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: COLORS.navy, fontWeight: 700 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "#fff", background: COLORS.gold, borderRadius: 3, padding: "1px 6px" }}>Dress code</span>
                    {day.dress}
                  </span>
                )}
                {day.subnote && (day.subnoteAlways || (aud !== "all" && (aud === NB || aud === ND))) && (
                  <span style={{ color: COLORS.parentsInk, fontStyle: "italic" }}>{day.subnote}</span>
                )}
              </div>
            )}
            {isMobile ? <AgendaDay day={day} /> : <TimelineDay day={day} aud={aud} />}
          </>
        ))}
    </section>
  );
}

/* ---------------- PDF (respects the audience filter) ---------------- */

const PDF_RGB = {
  navy: [0, 45, 98], gold: [163, 145, 97], red: [238, 49, 36], grid: [232, 227, 216],
  band: [252, 250, 245], inkGray: [107, 101, 87], who: [74, 84, 96],
  marker: [138, 124, 82], white: [255, 255, 255], cdd: [205, 214, 226],
  headFill: [251, 248, 241], markerFill: [250, 246, 236],
};

function loadScript(src) {
  return new Promise((res, rej) => {
    const s = document.createElement("script");
    s.src = src; s.async = true;
    s.onload = res; s.onerror = () => rej(new Error("Failed to load " + src));
    document.head.appendChild(s);
  });
}

async function ensureJsPdf() {
  if (!(window.jspdf && window.jspdf.jsPDF)) {
    await loadScript("https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js");
  }
  if (!(window.jspdf.jsPDF.API && window.jspdf.jsPDF.API.autoTable)) {
    await loadScript("https://cdn.jsdelivr.net/npm/jspdf-autotable@3.8.2/dist/jspdf.plugin.autotable.min.js");
  }
}

const msAud = (kind) => (kind === "bus" ? "Buses" : kind === "parents" ? "Parents" : "Boarding");

async function downloadSchedulePdf(days, opts) {
  await ensureJsPdf();
  const { jsPDF } = window.jspdf;
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const PW = doc.internal.pageSize.getWidth();
  const PH = doc.internal.pageSize.getHeight();
  const M = 40;
  const _now = new Date();
  const printedStamp = _now.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) + " \u00b7 " + _now.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

  doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(...PDF_RGB.gold);
  doc.text("KING'S ACADEMY", M, M);
  doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(...PDF_RGB.inkGray);
  doc.text("Printed on " + printedStamp, PW - M, M, { align: "right" });
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...PDF_RGB.navy); doc.setFontSize(20);
  doc.text(opts.docTitle, M, M + 22);
  doc.setFontSize(9); doc.setTextColor(...PDF_RGB.gold);
  doc.text(opts.subtitle, M, M + 36);
  doc.setDrawColor(...PDF_RGB.navy); doc.setLineWidth(1.5);
  doc.line(M, M + 44, PW - M, M + 44);
  doc.setDrawColor(...PDF_RGB.red); doc.setLineWidth(2.5);
  doc.line(M, M + 44, M + 70, M + 44);
  let y = M + 60;

  days.forEach((day) => {
    const items = [];
    (day.events || []).forEach((e) => {
      const base = e.tag || (LANE_STYLE[e.lane] && LANE_STYLE[e.lane].tag) || "";
      const aud = e.withParents ? (e.tag ? e.tag + " and parents" : "Students and parents") : base;
      items.push({ sort: e.s, time: e.t, label: e.l, aud, who: [e.w, e.n].filter(Boolean).join(" \u00b7 "), marker: false });
    });
    (day.milestones || []).forEach((m) => items.push({ sort: m.at, time: m.time, label: m.label, aud: msAud(m.kind), who: m.w || "", marker: true }));
    items.sort((a, b) => a.sort - b.sort || ((a.marker ? 0 : 1) - (b.marker ? 0 : 1)));

    if (y > PH - M - 80) { doc.addPage(); y = M; }

    const barH = 22;
    doc.setFillColor(...PDF_RGB.navy); doc.rect(M, y, PW - 2 * M, barH, "F");
    doc.setFillColor(...PDF_RGB.red); doc.rect(M, y, 5, barH, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.setTextColor(...PDF_RGB.white);
    doc.text(day.meta, M + 8, y + 15);
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(...PDF_RGB.cdd);
    doc.text(day.intl ? day.title : (day.pop || ""), PW - M - 8, y + 15, { align: "right" });
    y += barH;

    if (day.dress) {
      doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(...PDF_RGB.gold);
      doc.text("DRESS CODE: ", M, y + 12);
      const w1 = doc.getTextWidth("DRESS CODE: ");
      doc.setFont("helvetica", "normal"); doc.setTextColor(...PDF_RGB.navy);
      doc.text(day.dress, M + w1, y + 12);
      y += 16;
    }
    if (dayIsEmpty(day)) {
      doc.setFillColor(250, 246, 236); doc.rect(M, y, PW - 2 * M, 26, "F");
      doc.setDrawColor(...PDF_RGB.grid); doc.setLineWidth(0.5); doc.rect(M, y, PW - 2 * M, 26, "S");
      doc.setFont("helvetica", "italic"); doc.setFontSize(9); doc.setTextColor(...PDF_RGB.inkGray);
      doc.text("Nothing scheduled for you on this day.", M + 8, y + 17);
      y += 26 + 16;
      return;
    }

    const body = items.map((it) => [it.time, it.label, it.aud, it.who]);
    const markers = items.map((it) => it.marker);

    doc.autoTable({
      startY: y,
      head: [["Time", "Event", "For", "Who / Notes"]],
      body,
      margin: { left: M, right: M },
      styles: { font: "helvetica", fontSize: 9, cellPadding: 4, lineColor: PDF_RGB.grid, lineWidth: 0.5, textColor: [40, 40, 40], valign: "top", overflow: "linebreak" },
      headStyles: { fillColor: PDF_RGB.headFill, textColor: PDF_RGB.navy, fontStyle: "bold", fontSize: 8 },
      alternateRowStyles: { fillColor: PDF_RGB.band },
      columnStyles: {
        0: { cellWidth: 80, textColor: PDF_RGB.inkGray, fontStyle: "bold" },
        1: { fontStyle: "bold", textColor: PDF_RGB.navy },
        2: { cellWidth: 74, textColor: PDF_RGB.inkGray },
        3: { textColor: PDF_RGB.who },
      },
      didParseCell: (d) => {
        if (d.section === "body" && markers[d.row.index]) {
          d.cell.styles.fillColor = PDF_RGB.markerFill;
          if (d.column.index === 1) { d.cell.styles.textColor = PDF_RGB.marker; d.cell.styles.fontStyle = "italic"; }
        }
      },
      didDrawPage: () => {
        const p = doc.internal.getCurrentPageInfo().pageNumber;
        doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(...PDF_RGB.inkGray);
        doc.text(opts.footer, M, PH - 18);
        doc.text(String(p), PW - M, PH - 18, { align: "right" });
      },
    });
    y = doc.lastAutoTable.finalY + 16;
  });

  doc.save(opts.file + ".pdf");
}

/* ---------------- Upper School schedule ---------------- */

function UpperSchoolTimeline({ aud, onBack, onChangeAud }) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(() => DAYS.map(() => false));
  const toggle = (i) => setOpen((p) => p.map((v, idx) => (idx === i ? !v : v)));
  const [pdfBusy, setPdfBusy] = useState(null); // null | "self" | "master"

  const days = DAYS.map((d) => filterDay(d, aud));

  const pdfFor = async (which) => {
    setPdfBusy(which);
    try {
      if (which === "master") {
        await downloadSchedulePdf(DAYS.map((d) => filterDay(d, "all")), {
          docTitle: "Orientation Schedule",
          subtitle: "UPPER SCHOOL   \u00b7   2026\u20132027   \u00b7   MASTER SCHEDULE",
          footer: "King's Academy \u00b7 Orientation 2026\u20132027 \u00b7 Master Schedule",
          file: "Orientation_2026-2027_Master",
        });
      } else {
        await downloadSchedulePdf(days, {
          docTitle: "Orientation Schedule",
          subtitle: "UPPER SCHOOL   \u00b7   2026\u20132027   \u00b7   " + audLabel(aud).toUpperCase(),
          footer: "King's Academy \u00b7 Orientation 2026\u20132027 \u00b7 " + audLabel(aud),
          file: "Orientation_2026-2027_" + (aud === "all" ? "All" : audLabel(aud).replace(/[^A-Za-z]+/g, "_")),
        });
      }
    } catch (e) { window.print(); }
    finally { setPdfBusy(null); }
  };

  return (
    <div className="ofs-root" style={{ "--gutter": "74px", fontFamily: FONT_BODY, color: "#1d2733", lineHeight: 1.3, background: "radial-gradient(1200px 420px at 50% -140px,#fbf9f4,transparent), #E8E6DF", minHeight: "100vh" }}>
      <style>{`
        .evt { opacity: 0; animation: ofsRise .5s ease forwards; }
        .ms { opacity: 0; animation: ofsFade .5s ease forwards; }
        @keyframes ofsRise { from { opacity: 0; transform: translateY(7px); } to { opacity: 1; transform: none; } }
        @keyframes ofsFade { to { opacity: 1; } }
        .ofs-day-head:hover { filter: brightness(1.1); }
        .ofs-pdf-btn:hover { filter: brightness(1.14); }
        .ofs-link:hover { text-decoration: underline; }
        .ofs-back-btn:hover { background: #1d2733; color: #fff; }
        .ofs-card { transition: transform .15s ease, box-shadow .15s ease; }
        .ofs-card:hover { transform: translateY(-2px); box-shadow: 0 6px 18px rgba(35,60,95,.13); }
        @media (prefers-reduced-motion: reduce) { .evt, .ms { opacity: 1 !important; animation: none !important; } }
        @media (max-width: 600px) { .ofs-wrap { padding: 18px 12px 40px !important; } .ofs-title { font-size: 24px !important; } }
      `}</style>

      <div className="ofs-wrap" style={{ maxWidth: 1140, margin: "0 auto", padding: "26px 26px 60px" }}>
        <button type="button" onClick={onBack} className="ofs-back-btn" style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#fff", border: "1.5px solid #1d2733", borderRadius: 5, padding: "7px 13px", marginBottom: 16, color: "#1d2733", fontFamily: FONT_BODY, fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
          ← All schedules
        </button>

        <div style={{ borderBottom: `3px solid ${COLORS.navy}`, paddingBottom: 16, marginBottom: 8, display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 24, flexWrap: "wrap" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <h1 className="ofs-title" style={{ fontFamily: FONT_DISPLAY, color: COLORS.navy, fontSize: 29, lineHeight: 1.08, margin: "4px 0 0", fontWeight: 700 }}>Orientation Timeline</h1>
            <div style={{ color: COLORS.gold, fontWeight: 700, fontSize: 13, letterSpacing: ".05em", marginTop: 5 }}>UPPER SCHOOL &nbsp;·&nbsp; 2026–2027</div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, alignSelf: "flex-start" }}>
            <button type="button" onClick={() => pdfFor("self")} disabled={!!pdfBusy} className="ofs-pdf-btn" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7, background: COLORS.navy, color: "#fff", border: "none", borderRadius: 4, padding: "9px 15px", fontFamily: FONT_BODY, fontSize: 13, fontWeight: 700, cursor: pdfBusy ? "default" : "pointer", opacity: pdfBusy ? 0.7 : 1, whiteSpace: "nowrap" }}>
              <span style={{ fontSize: 15, lineHeight: 1 }}>⤓</span> {pdfBusy === "self" ? "Preparing…" : `PDF — ${audLabel(aud)}`}
            </button>
            <button type="button" onClick={() => pdfFor("master")} disabled={!!pdfBusy} className="ofs-pdf-btn" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7, background: "#fff", color: COLORS.navy, border: `1.5px solid ${COLORS.navy}`, borderRadius: 4, padding: "8px 15px", fontFamily: FONT_BODY, fontSize: 13, fontWeight: 700, cursor: pdfBusy ? "default" : "pointer", opacity: pdfBusy ? 0.7 : 1, whiteSpace: "nowrap" }}>
              <span style={{ fontSize: 15, lineHeight: 1 }}>⤓</span> {pdfBusy === "master" ? "Preparing…" : "Master Schedule (all students)"}
            </button>
          </div>
        </div>

        {/* audience chip */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", background: "#fff", border: `1px solid ${COLORS.grid}`, borderLeft: `5px solid ${COLORS.gold}`, borderRadius: 5, padding: "9px 14px", margin: "16px 0 4px" }}>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "#9b9384" }}>Showing</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.navy }}>{audLabel(aud)}</span>
          <button type="button" onClick={onChangeAud} className="ofs-link" style={{ background: "none", border: "none", padding: 0, color: COLORS.gold, fontFamily: FONT_BODY, fontSize: 13, fontWeight: 700, cursor: "pointer", marginLeft: "auto" }}>
            change
          </button>
        </div>
        {aud !== "all" && (
          <div style={{ fontSize: 11.5, color: "#8a7c52", fontStyle: "italic", margin: "0 0 6px 2px" }}>
            Parent events are included alongside your schedule, including check-in and paperwork.
          </div>
        )}

        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 20px", alignItems: "center", margin: "12px 0 6px", fontSize: 12.5, color: "#46505c" }}>
          <LegendItem swatch={<Swatch solid={COLORS.navy} />}>Boarding</LegendItem>
          <LegendItem swatch={<Swatch solid={COLORS.red} />}>Day students</LegendItem>
          <LegendItem swatch={<Swatch solid={COLORS.gold} />}>All students together</LegendItem>
          <LegendItem swatch={<Swatch solid={COLORS.parentsEdge} />}>Parents</LegendItem>
          <LegendItem swatch={<Swatch dashed />}>Buses</LegendItem>
          <LegendItem swatch={<Swatch dot />}>Point-in-time event</LegendItem>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "12px 0 2px", fontSize: 13, fontStyle: "italic", color: "#8a7c52" }}>
          <span style={{ fontSize: 11, color: COLORS.gold, fontStyle: "normal" }}>▶</span>
          Select any day below to expand its schedule.
        </div>

        {days.map((day, i) => (
          <DayCard key={i} day={day} open={open[i]} onToggle={() => toggle(i)} isMobile={isMobile} aud={aud} />
        ))}

        <p style={{ fontSize: 11.5, color: "#6b6557", marginTop: 16, lineHeight: 1.5 }}>
          Tap a day to open or close it.
        </p>
      </div>
    </div>
  );
}

/* ---------------- Landing: school, then audience ---------------- */

function Shell({ children }) {
  return (
    <div style={{ fontFamily: FONT_BODY, background: "radial-gradient(1200px 420px at 50% -140px,#fbf9f4,transparent), #E8E6DF", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 20px" }}>
      <style>{`
        .ofs-card { transition: transform .15s ease, box-shadow .15s ease; }
        .ofs-card:hover { transform: translateY(-2px); box-shadow: 0 6px 18px rgba(35,60,95,.13); }
        .ofs-link:hover { text-decoration: underline; }
        .ofs-back-btn:hover { background: #1d2733; color: #fff; }
      `}</style>
      <div style={{ width: "100%", maxWidth: 780, textAlign: "center" }}>{children}</div>
    </div>
  );
}

function Masthead({ sub }) {
  return (
    <>
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 2 }}>
        <Logo height={136} />
      </div>
      <h1 style={{ fontFamily: FONT_DISPLAY, color: COLORS.navy, fontSize: 34, lineHeight: 1.1, margin: "16px 0 6px", fontWeight: 700 }}>Orientation</h1>
      {sub && <div style={{ color: COLORS.gold, fontWeight: 700, fontSize: 13, letterSpacing: ".06em" }}>{sub}</div>}
      {sub && <div style={{ height: 3, background: COLORS.navy, margin: "20px auto 0", maxWidth: 120 }} />}
    </>
  );
}

const CARD = {
  background: "#fff", border: `1px solid ${COLORS.grid}`, borderRadius: 8,
  padding: "22px 22px", cursor: "pointer", textAlign: "left", fontFamily: FONT_BODY,
  boxShadow: "0 1px 3px rgba(35,60,95,.06)", display: "flex", flexDirection: "column", justifyContent: "space-between",
};

const gradeText = (g) => (/^\d+$/.test(g) ? "Grade " + g : g);

function StudentAdvisorList({ onBack }) {
  const isMobile = useIsMobile();
  const [pass, setPass] = useState("");
  const [rows, setRows] = useState(null); // null until unlocked
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [mode, setMode] = useState("student");

  const unlock = async () => {
    const code = pass.trim();
    if (!code) { setErr("Enter the passcode."); return; }
    setLoading(true); setErr("");
    try {
      const call = httpsCallable(getFunctions(), "getAdvisorList");
      const res = await call({ passcode: code });
      const students = (res.data && res.data.students) || [];
      setRows(students.map((r) => ({
        first: r.first || "", last: r.last || "", grade: r.grade || "", advisor: r.advisor || "",
        student: ((r.first || "") + " " + (r.last || "")).trim(),
      })));
    } catch (e) {
      const msg = (e && e.message) || "";
      setErr(/incorrect|permission|denied/i.test(msg) ? "Incorrect passcode." : "Couldn't load the list. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const header = (
    <>
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 4 }}><Logo height={92} /></div>
      <h1 style={{ fontFamily: FONT_DISPLAY, color: COLORS.navy, fontSize: 28, margin: "10px 0 4px", fontWeight: 700 }}>Student Advisor List</h1>
    </>
  );
  const backBtn = (
    <button type="button" onClick={onBack} className="ofs-back-btn"
      style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#fff", border: "1.5px solid #1d2733", borderRadius: 5, padding: "8px 14px", marginTop: 18, color: "#1d2733", fontFamily: FONT_BODY, fontSize: 15, fontWeight: 700, cursor: "pointer" }}>← Back</button>
  );

  if (rows === null) {
    return (
      <Shell>
        {header}
        <div style={{ background: "#fff", border: `1px solid ${COLORS.grid}`, borderRadius: 10, padding: isMobile ? 18 : 24, marginTop: 18, maxWidth: 420, marginLeft: "auto", marginRight: "auto", boxShadow: "0 1px 3px rgba(35,60,95,.06)" }}>
          <div style={{ color: "#6b6557", fontSize: 14, marginBottom: 14, lineHeight: 1.5 }}>Enter the passcode to view the list.</div>
          <input type="password" value={pass} onChange={(e) => setPass(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") unlock(); }}
            placeholder="Passcode" autoFocus
            style={{ width: "100%", boxSizing: "border-box", fontFamily: FONT_BODY, fontSize: 16, padding: "11px 12px", border: `1.5px solid ${COLORS.grid}`, borderRadius: 6, outline: "none", color: "#1d2733" }} />
          {err ? <div style={{ color: COLORS.red, fontSize: 13.5, fontWeight: 700, marginTop: 10 }}>{err}</div> : null}
          <button type="button" onClick={unlock} disabled={loading}
            style={{ width: "100%", marginTop: 14, background: loading ? "#9aa4b1" : COLORS.navy, color: "#fff", border: "none", borderRadius: 6, padding: "12px", fontFamily: FONT_BODY, fontSize: 15.5, fontWeight: 700, cursor: loading ? "default" : "pointer" }}>
            {loading ? "Checking…" : "View list"}
          </button>
        </div>
        {backBtn}
      </Shell>
    );
  }

  const query = q.trim().toLowerCase();
  const filtered = rows.filter(
    (r) => !query || r.student.toLowerCase().includes(query) || r.advisor.toLowerCase().includes(query) || r.grade.toLowerCase().includes(query)
  );

  let body;
  if (rows.length === 0) {
    body = <div style={{ color: "#6b6557", fontSize: 14, padding: "12px 4px" }}>The list is empty.</div>;
  } else if (filtered.length === 0) {
    body = <div style={{ color: "#6b6557", fontSize: 14, padding: "12px 4px" }}>No matches for "{q}".</div>;
  } else if (mode === "advisor") {
    const groups = {};
    filtered.forEach((r) => { (groups[r.advisor] = groups[r.advisor] || []).push(r); });
    const advisors = Object.keys(groups).sort((a, b) => a.localeCompare(b));
    body = advisors.map((adv) => (
      <div key={adv} style={{ marginBottom: 16 }}>
        <div style={{ fontFamily: FONT_DISPLAY, color: COLORS.navy, fontWeight: 700, fontSize: 16, padding: "6px 0 5px", borderBottom: `2px solid ${COLORS.gold}` }}>
          {adv || "Unassigned"} <span style={{ color: COLORS.gold, fontSize: 12.5, fontWeight: 700 }}>· {groups[adv].length}</span>
        </div>
        {groups[adv].slice().sort((a, b) => (a.last || a.student).localeCompare(b.last || b.student)).map((r, i) => (
          <div key={i} style={{ padding: "9px 4px", borderBottom: "1px solid #f0ece2", fontSize: 15, color: "#1d2733", display: "flex", justifyContent: "space-between", gap: 10 }}>
            <span>{r.student}</span>
            {r.grade ? <span style={{ color: "#8a7c52", fontSize: 12.5, fontWeight: 700, flex: "none" }}>{gradeText(r.grade)}</span> : null}
          </div>
        ))}
      </div>
    ));
  } else {
    body = filtered.slice().sort((a, b) => (a.last || a.student).localeCompare(b.last || b.student) || a.first.localeCompare(b.first)).map((r, i) => (
      <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, padding: "11px 4px", borderBottom: "1px solid #f0ece2" }}>
        <span style={{ fontSize: 15.5, fontWeight: 700, color: COLORS.navy }}>
          {r.student}
          {r.grade ? <span style={{ color: "#8a7c52", fontSize: 12.5, fontWeight: 700, marginLeft: 8 }}>{gradeText(r.grade)}</span> : null}
        </span>
        <span style={{ fontSize: 14, color: "#6b6557", textAlign: "right", flex: "none", maxWidth: "46%" }}>{r.advisor}</span>
      </div>
    ));
  }

  return (
    <Shell>
      {header}
      <div style={{ background: "#fff", border: `1px solid ${COLORS.grid}`, borderRadius: 10, padding: isMobile ? 14 : 20, marginTop: 18, textAlign: "left", boxShadow: "0 1px 3px rgba(35,60,95,.06)" }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a student or advisor…"
            style={{ flex: "1 1 200px", fontFamily: FONT_BODY, fontSize: 16, padding: "10px 12px", border: `1.5px solid ${COLORS.grid}`, borderRadius: 6, outline: "none", color: "#1d2733" }} />
          <div style={{ display: "flex", borderRadius: 6, overflow: "hidden", border: `1.5px solid ${COLORS.navy}` }}>
            {[["student", "By student"], ["advisor", "By advisor"]].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setMode(k)}
                style={{ padding: "9px 13px", fontSize: 13, fontWeight: 700, fontFamily: FONT_BODY, cursor: "pointer", border: "none", background: mode === k ? COLORS.navy : "#fff", color: mode === k ? "#fff" : COLORS.navy }}>{l}</button>
            ))}
          </div>
        </div>
        {body}
      </div>
      {backBtn}
    </Shell>
  );
}

function SchoolChooser({ onPick }) {
  const btn = (label, color, key) => (
    <button type="button" className="ofs-card" onClick={() => onPick(key)}
      style={{ ...CARD, flex: "1 1 240px", maxWidth: 340, minHeight: 120, alignItems: "center", justifyContent: "center", borderTop: `5px solid ${color}` }}>
      <h2 style={{ fontFamily: FONT_DISPLAY, color: COLORS.navy, fontSize: 24, margin: 0, fontWeight: 700, textAlign: "center" }}>{label}</h2>
    </button>
  );
  return (
    <Shell>
      <Masthead />
      <div style={{ display: "flex", gap: 20, flexWrap: "wrap", justifyContent: "center", marginTop: 34 }}>
        {btn("Upper School", COLORS.navy, "upper")}
        {btn("Middle School", COLORS.gold, "middle")}
      </div>
    </Shell>
  );
}

function AudienceChooser({ onPick, onBack, onAdvisors }) {
  return (
    <Shell>
      <Masthead sub="UPPER SCHOOL · 2026–2027" />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 14, textAlign: "left", marginTop: 30 }}>
        {AUDIENCES.map((a) => {
          const isAll = a.key === "all";
          return (
            <button key={a.key} type="button" className="ofs-card" onClick={() => onPick(a.key)}
              style={{ ...CARD, minHeight: 64, borderTop: `5px solid ${isAll ? COLORS.gold : COLORS.navy}`, gridColumn: isAll ? "1 / -1" : "auto" }}>
              <div style={{ fontFamily: FONT_DISPLAY, color: COLORS.navy, fontSize: 17, fontWeight: 700 }}>{a.label}</div>
            </button>
          );
        })}
      </div>
      <button type="button" onClick={onBack} className="ofs-back-btn" style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#fff", border: "1.5px solid #1d2733", borderRadius: 5, padding: "7px 13px", marginTop: 18, color: "#1d2733", fontFamily: FONT_BODY, fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
        ← Back
      </button>
    </Shell>
  );
}

/* ---------------- Middle School ---------------- */

const MS_HEAD = [{ at: 0, to: 100, label: "All Students and Families", bg: COLORS.navy }];
const MS_HEAD_STU = [{ at: 0, to: 100, label: "All Students", bg: COLORS.navy }];

const MS_DAYS = [
  {
    meta: "Saturday · August 15, 2026",
    title: "Day 1",
    pop: "Afternoon orientation with families",
    subnote: "All students and families are required to attend the Saturday orientation.",
    subnoteAlways: true,
    dress: "Casual dress for orientation day",
    wide: true,
    ppm: 5.5,
    axisStart: 780,
    axisEnd: 1020,
    headCols: MS_HEAD,
    events: [
      { s: 780, e: 810, lane: "shared", t: "1:00 – 1:30 pm", l: "Head of School Welcome", w: "Mrs. Penny Townsend", n: "Abdul Majeed Shoman Auditorium", tag: "Parents and Students" },
      { s: 810, e: 855, lane: "shared", t: "1:30 – 2:15 pm", l: "Registration", n: "Middle School Building", tag: "Parents and Students" },
      { s: 855, e: 885, lane: "shared", t: "2:15 – 2:45 pm", l: "Tour of Middle School Spaces", w: "Led by Middle School students" },
      { s: 885, e: 900, lane: "shared", t: "2:45 – 3:00 pm", l: "Opening Remarks — Ms. Tala Issa, Head of Middle School", n: "Lecture Hall", tag: "Parents and Students" },
      { s: 900, e: 930, lane: "shared", t: "3:00 – 3:30 pm", l: "Parent Information Session", n: "Lecture Hall", tag: "Parents" },
      { s: 945, e: 975, lane: "shared", t: "3:45 – 4:15 pm", l: "Meet Your Advisor", n: "Advisory Locations", tag: "Parents and Students" },
      { s: 975, e: 1005, lane: "shared", t: "4:15 – 4:45 pm", l: "Ice Cream Social", n: "Middle School Building" },
    ],
    milestones: [],
  },
  {
    meta: "Sunday · August 16, 2026",
    title: "Day 2",
    pop: "Assembly, advisory and orientation workshops",
    dress: "Uniform required: white or blue button-down shirt, school pants, and a school tie",
    wide: true,
    ppm: 2.8,
    axisEnd: 960,
    headCols: MS_HEAD_STU,
    events: [
      { s: 510, e: 540, lane: "shared", t: "8:30 – 9:00 am", l: "Assembly", n: "Lecture Hall" },
      { s: 540, e: 560, lane: "shared", t: "9:00 – 9:20 am", l: "Advisory Meeting", n: "Advisory Locations" },
      { s: 560, e: 615, lane: "shared", t: "9:20 – 10:15 am", l: "Workshop 1", w: "Living the Guiding Principles", n: "Various Locations" },
      { s: 615, e: 630, lane: "shared", t: "10:15 – 10:30 am", l: "Break", n: "Outside" },
      { s: 635, e: 685, lane: "shared", t: "10:35 – 11:25 am", l: "Workshop 2", w: "Expectations at King's Academy", n: "Various Locations" },
      { s: 690, e: 735, lane: "shared", t: "11:30 am – 12:15 pm", l: "Lunch", n: "The Hess Family Dining Hall" },
      { s: 740, e: 810, lane: "shared", t: "12:20 – 1:30 pm", l: "Co-curricular Fair", n: "Various Locations" },
      { s: 810, e: 900, lane: "shared", t: "1:30 – 3:00 pm", l: "Madaba Games", n: "Commencement Lawn" },
    ],
    milestones: [
      { at: 910, kind: "bus", time: "3:10 pm", label: "Departure" },
    ],
  },
  {
    meta: "Monday · August 17, 2026",
    title: "Day 3",
    pop: "Final workshop, mini-classes and Madaba Games",
    dress: "Uniform required: white or blue button-down shirt, school pants, and a school tie",
    wide: true,
    ppm: 3.4,
    axisEnd: 1020,
    headCols: MS_HEAD_STU,
    events: [
      { s: 480, e: 540, lane: "shared", t: "8:00 – 9:00 am", l: "Faculty Meeting", tag: "Faculty" },
      { s: 540, e: 550, lane: "shared", t: "9:00 – 9:10 am", l: "Advisory Meeting", n: "Advisory Locations" },
      { s: 555, e: 630, lane: "shared", t: "9:15 – 10:30 am", l: "Workshop 3", w: "Own Your Year: Organization and Middle School Success", n: "Various Locations" },
      { s: 630, e: 645, lane: "shared", t: "10:30 – 10:45 am", l: "Break", n: "Outside" },
      { s: 650, e: 710, lane: "shared", t: "10:50 – 11:50 am", l: "Workshop 4", w: "All Things Tech", n: "Various Locations" },
      { s: 720, e: 765, lane: "shared", t: "12:00 – 12:45 pm", l: "Lunch", n: "New Dining Hall" },
      { s: 770, e: 830, lane: "shared", t: "12:50 – 1:50 pm", l: "Mini-classes" },
      { s: 840, e: 900, lane: "shared", t: "2:00 – 3:00 pm", l: "Mini-classes" },
      { s: 900, e: 920, lane: "shared", t: "3:00 – 3:20 pm", l: "Reflection and Connection", n: "Advisory Locations" },
      { s: 930, e: 975, lane: "shared", t: "3:30 – 4:15 pm", l: "Co-curriculars" },
    ],
    milestones: [
      { at: 540, kind: "bus", time: "9:00 am", label: "Students Arrive" },
      { at: 1005, kind: "bus", time: "4:45 pm", label: "Bus Departs" },
    ],
  },
];

function MiddleSchoolTimeline({ onBack }) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(() => MS_DAYS.map(() => false));
  const toggle = (i) => setOpen((p) => p.map((v, idx) => (idx === i ? !v : v)));
  const [pdfBusy, setPdfBusy] = useState(false);

  const handlePdf = async () => {
    setPdfBusy(true);
    try {
      await downloadSchedulePdf(MS_DAYS, {
        docTitle: "Middle School Orientation",
        subtitle: "MIDDLE SCHOOL   \u00b7   2026\u20132027",
        footer: "King's Academy \u00b7 Middle School Orientation 2026\u20132027",
        file: "Middle_School_Orientation_2026-2027",
      });
    } catch (e) { window.print(); }
    finally { setPdfBusy(false); }
  };

  return (
    <div className="ofs-root" style={{ "--gutter": "74px", fontFamily: FONT_BODY, color: "#1d2733", lineHeight: 1.3, background: "radial-gradient(1200px 420px at 50% -140px,#fbf9f4,transparent), #E8E6DF", minHeight: "100vh" }}>
      <style>{`
        .evt { opacity: 0; animation: ofsRise .5s ease forwards; }
        .ms { opacity: 0; animation: ofsFade .5s ease forwards; }
        @keyframes ofsRise { from { opacity: 0; transform: translateY(7px); } to { opacity: 1; transform: none; } }
        @keyframes ofsFade { to { opacity: 1; } }
        .ofs-day-head:hover { filter: brightness(1.1); }
        .ofs-pdf-btn:hover { filter: brightness(1.14); }
        .ofs-link:hover { text-decoration: underline; }
        .ofs-back-btn:hover { background: #1d2733; color: #fff; }
        @media (prefers-reduced-motion: reduce) { .evt, .ms { opacity: 1 !important; animation: none !important; } }
        @media (max-width: 600px) { .ofs-wrap { padding: 18px 12px 40px !important; } .ofs-title { font-size: 24px !important; } }
      `}</style>

      <div className="ofs-wrap" style={{ maxWidth: 1140, margin: "0 auto", padding: "26px 26px 60px" }}>
        <button type="button" onClick={onBack} className="ofs-back-btn" style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#fff", border: "1.5px solid #1d2733", borderRadius: 5, padding: "7px 13px", marginBottom: 16, color: "#1d2733", fontFamily: FONT_BODY, fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
          ← All schedules
        </button>

        <div style={{ borderBottom: `3px solid ${COLORS.navy}`, paddingBottom: 16, marginBottom: 8, display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 24, flexWrap: "wrap" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <h1 className="ofs-title" style={{ fontFamily: FONT_DISPLAY, color: COLORS.navy, fontSize: 29, lineHeight: 1.08, margin: "4px 0 0", fontWeight: 700 }}>Student Orientation</h1>
            <div style={{ color: COLORS.gold, fontWeight: 700, fontSize: 13, letterSpacing: ".05em", marginTop: 5 }}>MIDDLE SCHOOL &nbsp;·&nbsp; 2026–2027</div>
          </div>
          <button type="button" onClick={handlePdf} disabled={pdfBusy} className="ofs-pdf-btn" style={{ alignSelf: "flex-start", display: "inline-flex", alignItems: "center", gap: 7, background: COLORS.navy, color: "#fff", border: "none", borderRadius: 4, padding: "9px 15px", fontFamily: FONT_BODY, fontSize: 13, fontWeight: 700, cursor: pdfBusy ? "default" : "pointer", opacity: pdfBusy ? 0.7 : 1 }}>
            <span style={{ fontSize: 15, lineHeight: 1 }}>⤓</span> {pdfBusy ? "Preparing…" : "Download PDF"}
          </button>
        </div>

        <p style={{ fontSize: 13.5, color: "#4a5460", lineHeight: 1.55, margin: "14px 0 4px", maxWidth: 780 }}>
          Orientation helps students learn the essentials of being part of our community. All students
          attend five workshops in a rotation: <b style={{ color: COLORS.navy }}>Living the Guiding Principles</b>,{" "}
          <b style={{ color: COLORS.navy }}>Expectations at King's Academy</b>,{" "}
          <b style={{ color: COLORS.navy }}>Own Your Year</b>, <b style={{ color: COLORS.navy }}>All Things Tech</b>, and{" "}
          <b style={{ color: COLORS.navy }}>The Middle School Community</b>.
        </p>

        <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "14px 0 2px", fontSize: 13, fontStyle: "italic", color: "#8a7c52" }}>
          <span style={{ fontSize: 11, color: COLORS.gold, fontStyle: "normal" }}>▶</span>
          Select any day below to expand its schedule.
        </div>

        {MS_DAYS.map((day, i) => (
          <DayCard key={i} day={day} open={open[i]} onToggle={() => toggle(i)} isMobile={isMobile} aud="all" />
        ))}

        <p style={{ fontSize: 11.5, color: "#6b6557", marginTop: 16, lineHeight: 1.5 }}>
          Tap a day to open or close it.
        </p>
      </div>
    </div>
  );
}

export default function OpeningOfSchool() {
  const [school, setSchool] = useState(null);   // null | "upper" | "middle"
  const [aud, setAud] = useState(null);         // null | audience key

  if (school === "advisors") return <StudentAdvisorList onBack={() => setSchool("upper")} />;
  if (school === "middle") return <MiddleSchoolTimeline onBack={() => setSchool(null)} />;

  if (school === "upper") {
    if (!aud) return <AudienceChooser onPick={setAud} onBack={() => { setSchool(null); setAud(null); }} onAdvisors={() => setSchool("advisors")} />;
    return (
      <UpperSchoolTimeline
        aud={aud}
        onBack={() => { setSchool(null); setAud(null); }}
        onChangeAud={() => setAud(null)}
      />
    );
  }

  return <SchoolChooser onPick={(s) => {
    if (s === "middle") { setSchool("middle"); }
    else if (s === "master") { setAud("all"); setSchool("upper"); }
    else { setAud(null); setSchool("upper"); }
  }} />;
}